import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface DateOfBirthInputProps {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  error?: string;
}

export function DateOfBirthInput({ value, onChange, error }: DateOfBirthInputProps) {
  const [day, setDay] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [year, setYear] = useState<string>('');

  // Initialize from value prop
  useEffect(() => {
    if (value) {
      setDay(String(value.getDate()));
      setMonth(String(value.getMonth() + 1));
      setYear(String(value.getFullYear()));
    }
  }, [value]);

  // Update parent when all fields are selected
  useEffect(() => {
    if (day && month && year) {
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      onChange(date);
    } else {
      onChange(undefined);
    }
  }, [day, month, year, onChange]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <div className="space-y-2">
      <Label htmlFor="dob">Date of Birth</Label>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className={cn("h-12 sm:h-10", error && "border-destructive")}>
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            {months.map((m, idx) => (
              <SelectItem key={idx} value={String(idx + 1)}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={day} onValueChange={setDay}>
          <SelectTrigger className={cn("h-12 sm:h-10", error && "border-destructive")}>
            <SelectValue placeholder="Day" />
          </SelectTrigger>
          <SelectContent>
            {days.map((d) => (
              <SelectItem key={d} value={String(d)}>
                {d}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className={cn("h-12 sm:h-10", error && "border-destructive")}>
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
      
      <p className="text-xs text-muted-foreground">
        Required for age verification. You must be 13+ to join Dino.
      </p>
    </div>
  );
}
